/*!
 * Copyright 2022 Quisitive Technologies, LLC. All rights reserved.
 */
import {
  compact,
  each,
  get,
  has,
  isArray,
  isObject,
  keys,
  map,
  omit,
  isEmpty
} from 'lodash';
import moment from 'moment';
import { relationsShip } from '../constants';

export function consent(answers) {
  const parsedAnswers = {
    consent: answers[7].selected
  };
  return parsedAnswers;
}

export function medicalHistory(answers) {
  const raw = answers[0];
  // Used constants to compare the value in loop
  const active = 'Currently being treated for or active';
  const resolved = 'In the past & resolved';
  const present = [];
  const past = [];
  const surgery = [];
  const visits = [];
  Object.keys(raw).map(questionId => {
    const medicalIssues = raw[questionId];
    Object.keys(medicalIssues).map(medicalKey => {
      const medIssue = medicalIssues[medicalKey];
      if (medIssue === active) {
        const activeObj = {};
        activeObj.issue = medicalKey;
        present.push(activeObj);
      } else if (medIssue === resolved) {
        const pastObj = {};
        pastObj.issue = medicalKey;
        past.push(pastObj);
      }
    });
    // used specific question id as we need to look for checkbox selected for surgery only case among 9 steps
    if (questionId === 'medical_gae3ca') {
      const arr = raw[questionId]?.selected;
      const otherText = raw[questionId]?.others;
      arr.forEach(item1 => {
        const surgeryObj = {};
        surgeryObj.issue = item1 !== 'Other' ? item1 : `${item1}-${otherText}`;
        if (item1 !== 'Other' || !isEmpty(otherText?.trim()))
          surgery.push(surgeryObj);
      });
    }
    // used specific question id as we need to look for checkbox selected for visits only case among 9 steps
    if (questionId === 'medical_6EL1gI') {
      const visitObj = {};
      let txt = raw[questionId]?.selected;
      const otherText = raw[questionId]?.others;
      const otherValIndex = txt.indexOf('Other');
      if (otherValIndex > -1) {
        txt += `-${otherText}`;
      }
      const cbSelected = raw[questionId]?.checkboxSelected;
      if (cbSelected && cbSelected.length > 0) {
        txt += `-${cbSelected.join(',')}`;
      }
      visitObj.issue = txt;
      visits.push(visitObj);
    }
  });
  return { medicalHistory: { past, present, surgery, visits } };
}

export function familyHistory(answers) {
  const raw = answers[0];
  let parsedAnswer = {};
  let newDisease = '';
  let doRecordNewDisease = 'No';
  // Here we are looping through each object and consolidating diseases for each relation
  Object.keys(raw).forEach(disease => {
    const eachDiseaseObj = raw[disease];
    Object.keys(eachDiseaseObj).forEach(relationsList => {
      const relations = eachDiseaseObj[relationsList];
      if (Array.isArray(relations)) {
        relations.forEach(relation => {
          if (parsedAnswer.hasOwnProperty(relation)) {
            const tempArr = parsedAnswer[relation];
            tempArr.push({ description: relationsList });
            parsedAnswer[relation] = tempArr;
          } else {
            parsedAnswer[relation] = [{ description: relationsList }];
          }
        });
      } else if (
        // Recording if new disease has been added
        relationsList === 'value' &&
        typeof relations === 'string' &&
        relations !== ''
      ) {
        // For new disease , patient selects the relationship in checkbox , so replacing the value of checkbox with new disease to pass down consolidating as per relations
        newDisease = relations;
      } else if (relationsList === 'selected') {
        doRecordNewDisease = relations;
      }
    });
  });
  // Below loop is to check if patient has already selected new disease and then unselected it later.
  if (newDisease !== '') {
    Object.keys(parsedAnswer).forEach(relation => {
      parsedAnswer[relation].map((relationObj, index) => {
        if (relationObj.description === 'selected') {
          if (doRecordNewDisease === 'Yes') {
            relationObj.description = newDisease;
          } else {
            parsedAnswer[relation].splice(index, 1);
          }
        }
      });
      parsedAnswer[relationsShip[relation]] = parsedAnswer[relation];
      parsedAnswer = omit(parsedAnswer, relation);
    });
  } else {
    Object.keys(parsedAnswer).forEach(relation => {
      parsedAnswer[relationsShip[relation]] = parsedAnswer[relation];
      parsedAnswer = omit(parsedAnswer, relation);
    });
  }

  return parsedAnswer;
}

export function socialHistory(answers) {
  const parsedAnswers = {
    smoking: {
      current: answers[0]?.social_9VFL5S?.selected || 'No',
      past: answers[0]?.social_HUGvM1?.selected || 'No'
    }
    //, alcohol: {
    //   frequency: answers[3].selected,
    //   quantity: answers[4].selected,
    //   heavyDrinking: answers[5].selected
    // }
  };
  return parsedAnswers;
}

export function medications(answers, parsed) {
  const medications = [];
  const answerObj = answers[0];
  const allergies = [];

  const algAnswers = get(
    answerObj,
    `${parsed.allergies.questionId}.${parsed.allergies.answerKey}`,
    ''
  );

  const otherText = get(answerObj, `${parsed.allergies.questionId}.others`, '');
  algAnswers.forEach(item => {
    const surveyObj = {};
    surveyObj.issue = item !== 'Other' ? item : `${item}-${otherText}`;

    if (item !== 'Other' || !isEmpty(otherText?.trim()))
      allergies.push(surveyObj);
  });

  map(parsed.medications, med => {
    const answer = get(answerObj, `${med.questionId}.${med.answerKey}`, '');
    const otherText = get(answerObj, `${med.questionId}.others`, '');
    if (isArray(answer)) {
      map(answer, ans => {
        const surveyObj = {};
        surveyObj.issue = isObject(ans)
          ? ans.value
          : ans !== 'Other'
          ? ans
          : `${ans}-${otherText}`;
        if (ans !== 'Other' || !isEmpty(otherText?.trim()))
          medications.push(surveyObj);
      });
    } else if (med.questionId == 'medication_s1LCr5') {
      answer && medications.push({ issue: `Other-${answer}` });
    } else {
      answer && medications.push({ issue: answer });
    }
  });

  return { allergies, medications };
}

export function sleep(answers, parsed) {
  let newAnswers = JSON.parse(JSON.stringify(answers));
  newAnswers[0].sleep_6OxKpn[
    'time'
  ] = moment(
    `${newAnswers[0].sleep_6OxKpn['time']} ${newAnswers[0].sleep_6OxKpn['meridiem']}`,
    ['h:mm A']
  ).format('HH:mm');
  newAnswers[0].sleep_f9laFO[
    'time'
  ] = moment(
    `${newAnswers[0].sleep_f9laFO['time']} ${newAnswers[0].sleep_f9laFO['meridiem']}`,
    ['h:mm A']
  ).format('HH:mm');

  return parseAnswers(newAnswers, parsed);
}

export function physicalActivity(answers, parsed) {
  let parsedAnswers = {};
  let newObject = parseAnswers(answers, parsed);
  Object.entries(newObject).map((obj, key) => {
    let newKey = obj[0];
    parsedAnswers[newKey] = obj[1] === '' ? '0' : obj[1];
  });
  return parsedAnswers;
}

export function nutrition(answers, parsed) {
  const parsedAnswers = {};
  const answerObj = answers[0];
  map(parsed, (val, key) => {
    const obj = {};
    const times = get(answerObj, `${val.freqency}.times`);
    const option = get(answerObj, `${val.freqency}.option`);
    const frequency = calculateNutritionFreq(times, option);
    if (val.names) {
      const names = map(
        val.names,
        id =>
          get(answerObj, `${id}.formItems.0.other`) ||
          get(answerObj, `${id}.formItems.0.selected`)
      );
      obj.names = compact(names);
    }
    obj.frequency = frequency;
    parsedAnswers[key] = obj;
  });
  return parsedAnswers;
}

export function mentalHealth(answers, parsed) {
  let parsedAnswers = {};
  let newObject = parseAnswers(answers, parsed);
  Object.entries(newObject).map((obj, key) => {
    let newKey = obj[0];
    parsedAnswers[newKey] = obj[1] === '' ? 'Not at all' : obj[1];
  });
  return parsedAnswers;
}

const parseAnswers = (answers, parsed) => {
  const parsedAnswers = {};
  const answerObj = answers[0];
  map(parsed, (val, key) => {
    if (isObject(val)) {
      if (val.nestedObject) {
        parsedAnswers[key] = parseAnswers(answers, val);
      } else {
        parsedAnswers[key] = get(
          answerObj,
          `${val.questionId}.${val.answerKey}`,
          ''
        );
      }
    }
  });
  return parsedAnswers;
};

const nutRates = {
  'times per day': 1,
  'times per week': 7,
  'times per month': 30
};

function calculateNutritionFreq(num, rate) {
  if (!num || !rate) return 0;
  return parseInt(num, 10) / nutRates[rate];
}

function getCerealNames(cereals) {
  const cerealList = [];
  if (!cereals || !cereals.map) return [];
  cereals.map(cereal => {
    cerealList.push(cereal.selected);
    return false;
  });
  return cerealList;
}

export const isAllSurveysCompleted = surveys => {
  let completed = true;
  for (let i = 0; i < surveys.length; i++) {
    const survey = surveys[i];
    const done = isSurveyCompleted(survey, get(survey, 'answers.raw[0]', {}));
    if (!done) {
      completed = false;
      break;
    }
  }
  return completed;
};

export const isSurveyCompleted = (survey, answers) => {
  return isAllStepsCompleted(survey.steps, answers);
};

export const isSurveyPartiallyCompleted = (survey, answers) => {
  return isStepsPartiallyCompleted(survey?.steps, answers);
};

export const isStepsPartiallyCompleted = (steps, answers) => {
  let partiallyCompleted = false;
  for (let i = 0; i < steps?.length; i++) {
    const step = steps[i];
    const answered = isAnyQuestionAnswered(step, answers);
    if (answered) {
      partiallyCompleted = true;
      break;
    }
  }

  return partiallyCompleted;
};

export const isAnyQuestionAnswered = (step, answers) => {
  let answered = false;
  if (step) {
    const { questions } = step;
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (has(question, 'display') && !question.display) {
        continue;
      }
      const answer = answers[question.questionId];
      if (answer) {
        answered = true;
        break;
      }
    }
  }
  return answered;
};

export const isAllStepsCompleted = (steps, answers) => {
  let allStepsCompleted = true;
  each(steps, step => {
    step.completed = isAllQuestionsAnswered(step, answers, false);
    if (!step.completed) {
      allStepsCompleted = false;
    }
  });
  return allStepsCompleted;
};

export const isAllQuestionsAnswered = (
  step,
  answers,
  isOptAnswered = false
) => {
  let allAnswered = false;
  if (step) {
    const { questions } = step;
    for (let i = 0; i < questions.length; i++) {
      // medical history - others validation
      // medical history
      if (step.stepId === 'medical_step_5') {
        let arrayOfOther = answers.medical_gae3ca ? answers.medical_gae3ca : [];
        let otherData = arrayOfOther.selected ? arrayOfOther.selected : [];

        if (otherData.includes('Other') && arrayOfOther.others.trim() === '') {
          allAnswered = false;
          break;
        }
      }

      if (step.stepId === 'medical_step_6') {
        let arrayOfOther = answers.medical_6EL1gI ? answers.medical_6EL1gI : [];
        let otherData = arrayOfOther.selected ? arrayOfOther.selected : [];
        if (otherData.includes('Other') && arrayOfOther.other.trim() === '') {
          allAnswered = false;
          break;
        }
      }
      if (step.stepId === 'medications_step_1') {
        let noneSelected =
          answers.medication_s154OE && answers.medication_s154OE.selected
            ? answers.medication_s154OE.selected.length
            : 0;
        if (noneSelected > 0){
          //answers.medication_s1BxU1.selected = [];
          answers.medication_s1LCr5.value="";
          allAnswered= true;
          return allAnswered;
        }

        let dropdownSelected =
          answers.medication_s1BxU1 && answers.medication_s1BxU1.selected
            ? answers.medication_s1BxU1.selected.length
            : 0;
        let otherValue = answers.medication_s1LCr5
          ? answers.medication_s1LCr5.value?.trim()?.length
          : 0;

          
        allAnswered = noneSelected > 0 || dropdownSelected > 0 || otherValue > 0
       return allAnswered;
      }

      //allergies and medications
      if (step.stepId === 'medications_step_0') {
        let arrayOfOther = answers.medication_SDyPGo
          ? answers.medication_SDyPGo
          : [];
        let otherData = arrayOfOther.selected ? arrayOfOther.selected : [];

        if (otherData.includes('Other') && arrayOfOther.others.trim() === '') {
          allAnswered = false;
          break;
        }
      }
      //allergies and medications
      if (step.stepId === 'medications_step_2') {
        let arrayOfOther = answers.medication_SkkFXE
          ? answers.medication_SkkFXE
          : [];
        let otherData = arrayOfOther.selected ? arrayOfOther.selected : [];

        if (otherData.includes('Other') && arrayOfOther.others.trim() === '') {
          allAnswered = false;
          break;
        }
      }
      // --end
      const question = questions[i];
      if (has(question, 'display') && !question.display) {
        continue;
      }
      if (has(question, 'required') && !question.required) {
        if (isOptAnswered || questions.length === 1) {
          allAnswered = true;
          break;
        }

        continue;
      }
      if (keys(answers).includes(question.questionId)) {
        if (
          has(question, 'details.text.required') &&
          question.details.text.required
        ) {
          const answer = answers[question.questionId];
          if (get(answer, 'selected').length > 0) {
            if (get(answer, 'selected').includes('Other')) {
              // if (get(answer, 'Other_')) {
              if (true) {
                allAnswered = true;
                break;
              }
            } else {
              allAnswered = true;
              break;
            }
          }

          continue;
        }
      }

      if (isOptAnswered) {
        allAnswered = true;
      }
      const answer = answers[question.questionId];
      allAnswered = get(answer, 'answered', false);
      if (!allAnswered) {
        break;
      }
    }
  }

  return allAnswered;
};
